import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import { useAuth } from "./AuthContext";
import { useTranslation } from "./i18n/useTranslation";
import { FASTAPI_BASE_URL } from "./config";

// checkout-success is NOT idempotent (it extends access every time it runs), and
// the navigator remounts this screen when the restricted flag flips. Remember the
// confirmation per Stripe session so a remount never confirms twice.
const confirmations = new Map();

const confirmOnce = (sessionId, fetchWithAuth) => {
  if (!confirmations.has(sessionId)) {
    const attempt = (async () => {
      try {
        const res = await fetchWithAuth(
          `${FASTAPI_BASE_URL}/payments/checkout-success?session_id=${encodeURIComponent(sessionId)}`
        );
        const json = await res.json().catch(() => ({}));
        if (!res.ok) return { ok: false, error: json?.detail || "Payment confirmation failed." };
        return { ok: true };
      } catch (e) {
        return { ok: false, error: String(e?.message || e) };
      }
    })().then((result) => {
      if (!result.ok) confirmations.delete(sessionId); // allow a retry
      return result;
    });
    confirmations.set(sessionId, attempt);
  }
  return confirmations.get(sessionId);
};

export default function PaymentSuccessScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { t } = useTranslation();
  const { fetchWithAuth, authToken, refetchProfile, user, logout } = useAuth();

  const [status, setStatus] = useState("loading"); // loading | success | confirming | error
  const [error, setError] = useState("");
  const mountedRef = useRef(true);

  const sessionIdFromRoute = route?.params?.sessionId || "";
  const sessionIdFromUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    try {
      const u = new URL(window.location.href);
      return u.searchParams.get("session_id") || "";
    } catch {
      return "";
    }
  }, []);
  const sessionId = sessionIdFromRoute || sessionIdFromUrl;

  // A restricted user has no AITutor route, so Back to App is only offered once the
  // refreshed profile says the restriction is gone.
  const restricted = user?.subscription_restricted === true;

  const run = useCallback(async () => {
    setStatus("loading");
    setError("");
    if (!sessionId) {
      setStatus("error");
      setError("Missing session ID.");
      return;
    }
    const result = await confirmOnce(sessionId, fetchWithAuth);
    if (!mountedRef.current) return;
    if (!result.ok) {
      setStatus("error");
      setError(result.error);
      return;
    }
    // The fresh profile (not the earlier 402 flag) decides whether access is back.
    let stillRestricted = false;
    if (refetchProfile) {
      try {
        const profile = await refetchProfile();
        stillRestricted = profile?.subscription_restricted === true;
      } catch {
        // Could not verify access is back: stay in the confirming state, never claim success.
        stillRestricted = true;
      }
    }
    if (!mountedRef.current) return;
    setStatus(stillRestricted ? "confirming" : "success");
  }, [sessionId, fetchWithAuth, refetchProfile]);

  useEffect(() => {
    mountedRef.current = true;
    run();
    return () => { mountedRef.current = false; };
  }, [sessionId]);

  const handleBack = () => {
    if (authToken) {
      navigation.navigate("AITutor");
    } else {
      navigation.navigate("Login");
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        {status === "loading" && (
          <>
            <ActivityIndicator size="large" color="#2563eb" />
            <Text style={styles.title}>Finalizing your payment...</Text>
          </>
        )}
        {status === "success" && (
          <>
            <Text style={styles.title}>Payment successfully completed</Text>
            <Text style={styles.subtitle}>
              Your plan is now active. Thank you for upgrading!
            </Text>
          </>
        )}
        {status === "confirming" && (
          <>
            <Text style={styles.title}>{t("We're confirming your payment…")}</Text>
            <Text style={styles.subtitle}>
              {t("Your plan isn't active yet. This can take a moment. Check again in a few seconds, or log out and log back in.")}
            </Text>
          </>
        )}
        {status === "error" && (
          <>
            <Text style={styles.title}>Payment confirmation failed</Text>
            <Text style={styles.subtitle}>{error || "Please contact support."}</Text>
          </>
        )}

        {(restricted || status === "confirming") && status !== "loading" ? (
          <>
            <TouchableOpacity onPress={run} style={styles.cta}>
              <Text style={styles.ctaText}>{t("Check payment again")}</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => logout()} style={styles.secondaryCta}>
              <Text style={styles.secondaryCtaText}>{t("Log out")}</Text>
            </TouchableOpacity>
          </>
        ) : (
          <TouchableOpacity onPress={handleBack} style={styles.cta}>
            <Text style={styles.ctaText}>Back to App</Text>
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f3f6fb",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  card: {
    width: "90%",
    maxWidth: 520,
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    alignItems: "center",
  },
  title: {
    fontSize: 20,
    fontWeight: "700",
    marginTop: 12,
    textAlign: "center",
  },
  subtitle: {
    fontSize: 14,
    color: "#475569",
    textAlign: "center",
    marginTop: 8,
  },
  cta: {
    marginTop: 20,
    paddingVertical: 12,
    paddingHorizontal: 18,
    borderRadius: 10,
    backgroundColor: "#2563eb",
  },
  ctaText: {
    color: "#fff",
    fontWeight: "700",
  },
  secondaryCta: {
    marginTop: 10,
    paddingVertical: 10,
    paddingHorizontal: 18,
  },
  secondaryCtaText: {
    color: "#2563eb",
    fontWeight: "700",
  },
});
